import { Text } from '@/components/ui/text';
import { LandingGlow } from '@/features/public-web/LandingGlow';
import { flushPostHog, trackLandingCTAClicked } from '@/lib/analytics/posthog';
import { APP_CONFIG } from '@/lib/app/config';
import { Stack, useRouter } from 'expo-router';
import * as React from 'react';
import { Image, Pressable, ScrollView, View } from 'react-native';
import Svg, { Path } from 'react-native-svg';

const DOWNLOAD_URL = 'https://radious.app/download';
const WORDMARK_URL = '/static/radious-wordmark-light.svg';
const QR_IMAGE_URL = '/static/qr-download.svg';
type DownloadPath = '/' | '/events';

function withLandingTracking(target: string) {
  if (typeof window === 'undefined') {
    return target;
  }

  try {
    const source = new URL(window.location.href);
    const destination = new URL(target, window.location.origin);
    for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'qr_slug']) {
      const value = source.searchParams.get(key);
      if (value && !destination.searchParams.get(key)) {
        destination.searchParams.set(key, value);
      }
    }
    return destination.toString();
  } catch {
    return target;
  }
}

function isIOSBrowser() {
  return typeof navigator !== 'undefined' && /iPad|iPhone|iPod/i.test(navigator.userAgent);
}

async function openExternalURL(url: string) {
  await flushPostHog();
  if (typeof window !== 'undefined') {
    window.location.assign(withLandingTracking(url));
  }
}

async function copyText(value: string) {
  if (navigator.clipboard) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.cssText = 'position:fixed;opacity:0;';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
}

export default function DownloadWebScreen() {
  const { push } = useRouter();
  const [copied, setCopied] = React.useState(false);

  const pushLandingCTA = React.useCallback(
    (label: string, destination: string, path: DownloadPath) => {
      trackLandingCTAClicked({ label, destination, href: path, pageName: 'download' });
      push(path);
    },
    [push]
  );

  const openLandingCTA = React.useCallback((label: string, destination: string, url: string) => {
    trackLandingCTAClicked({ label, destination, href: url, pageName: 'download' });
    void openExternalURL(url);
  }, []);

  React.useEffect(() => {
    if (isIOSBrowser()) {
      trackLandingCTAClicked({
        label: 'download_ios_auto_redirect',
        destination: 'app_store',
        href: APP_CONFIG.appStoreURL,
        pageName: 'download',
      });
      void flushPostHog().finally(() => {
        window.location.replace(withLandingTracking(APP_CONFIG.appStoreURL));
      });
    }
  }, []);

  const copyDownloadURL = React.useCallback(() => {
    trackLandingCTAClicked({
      label: 'download_copy_link',
      destination: 'copy_link',
      href: DOWNLOAD_URL,
      pageName: 'download',
    });
    copyText(DOWNLOAD_URL)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {});
  }, []);

  return (
    <View className="flex-1 bg-[#070a10]">
      <Stack.Screen options={{ headerShown: false, title: 'Download Radious' }} />
      <LandingGlow />

      <ScrollView
        className="flex-1"
        contentContainerClassName="mx-auto min-h-screen w-full max-w-[1080px] px-5 pb-7 pt-5 md:px-7 md:pb-5 md:pt-6"
        showsVerticalScrollIndicator={false}>
        <View className="flex-row items-center justify-between">
          <Pressable
            accessibilityRole="link"
            onPress={() => pushLandingCTA('download_wordmark_home', 'home', '/')}>
            <Image
              accessibilityLabel="Radious home"
              className="h-[19px] w-[92px]"
              resizeMode="contain"
              source={{ uri: WORDMARK_URL }}
            />
          </Pressable>
          <Pressable
            accessibilityRole="link"
            onPress={() => pushLandingCTA('download_back_home', 'home', '/')}>
            <Text className="text-xs font-semibold text-[#c4b69e] no-underline">
              {'\u2190 Radious'}
            </Text>
          </Pressable>
        </View>

        <View className="flex-1 gap-0 py-8 md:min-h-[560px] md:flex-row md:items-center md:gap-12 md:pb-10">
          <View className="flex-1">
            <View className="mb-5 flex-row items-center gap-2">
              <View className="size-1.5 rounded-full bg-primary shadow-md shadow-primary" />
              <Text className="text-[11px] font-bold uppercase tracking-[2.5px] text-[#7a6e60] no-underline">
                Live in San Antonio
              </Text>
            </View>

            <Text className="mb-[18px] text-[46px] font-extrabold uppercase leading-none tracking-[-0.5px] text-[#f5eddf] no-underline md:text-[72px]">
              Find your{'\n'}
              <Text className="text-[46px] font-extrabold uppercase leading-none tracking-[-0.5px] text-primary no-underline md:text-[72px]">
                night.
              </Text>
            </Text>

            <Text className="mb-7 max-w-[400px] text-[15px] leading-[26px] text-[#c4b69e] no-underline">
              Bars, pop-ups, live music, and last-minute plans, all of San Antonio&apos;s nights in
              your pocket.
            </Text>

            <View className="mb-5 max-w-[400px] gap-2.5 border-t border-white/10 pt-3">
              <FeatureItem label="Tonight&apos;s plans, not next weekend&apos;s" />
              <FeatureItem label="See what your friends have planned" />
              <FeatureItem label="Only San Antonio — hand-curated, no noise" />
            </View>

            <View className="max-w-[400px] gap-2">
              <Pressable
                accessibilityRole="link"
                className="min-h-[50px] flex-row items-center justify-center gap-2.5 rounded-[7px] bg-primary px-[30px] py-[15px] shadow-xl shadow-primary/20 active:opacity-80"
                onPress={() =>
                  openLandingCTA('download_app_store', 'app_store', APP_CONFIG.appStoreURL)
                }>
                <AppleIcon color="#ffffff" />
                <Text className="text-sm font-bold text-white no-underline">
                  Download on the App Store
                </Text>
              </Pressable>
              <Text className="py-0.5 text-center text-xs text-[#c4b69e] no-underline">
                Free · iPhone
              </Text>

              <Pressable
                accessibilityRole="link"
                className="min-h-[45px] items-center justify-center rounded-[7px] border border-white/15 px-7 py-[13px] active:opacity-80"
                onPress={() => pushLandingCTA('download_open_web_app', 'web_app', '/events')}>
                <Text className="text-[13px] font-semibold text-[#c4b69e] no-underline">
                  Open Web App
                </Text>
              </Pressable>

              {APP_CONFIG.playStoreURL ? (
                <Pressable
                  accessibilityRole="link"
                  className="min-h-[45px] flex-row items-center justify-center gap-2 rounded-[7px] border border-white/15 px-7 py-[13px] active:opacity-80"
                  onPress={() =>
                    openLandingCTA('download_google_play', 'google_play', APP_CONFIG.playStoreURL)
                  }>
                  <PlayIcon color="#c4b69e" />
                  <Text className="text-[13px] font-semibold text-[#c4b69e] no-underline">
                    Get it on Google Play
                  </Text>
                </Pressable>
              ) : null}
            </View>

            <View className="mt-8 flex-row flex-wrap items-center gap-2">
              <Text className="text-xs text-[#7a6e60] no-underline">Share with friends:</Text>
              <Pressable
                accessibilityRole="button"
                className="flex-row items-center gap-1 rounded-[5px] border border-white/15 py-1 pl-[7px] pr-[9px] active:opacity-80"
                onPress={copyDownloadURL}>
                <CopyIcon color={copied ? '#ef4e2e' : '#c4b69e'} />
                <Text
                  className={
                    copied
                      ? 'text-[11px] font-semibold text-primary no-underline'
                      : 'text-[11px] font-semibold text-[#c4b69e] no-underline'
                  }>
                  {copied ? 'Copied!' : 'radious.app/download'}
                </Text>
              </Pressable>
            </View>
          </View>

          <View className="hidden flex-1 items-center md:flex">
            <View className="relative w-full max-w-[300px] overflow-hidden rounded-[20px] border border-white/15 bg-[#111620] px-[22px] pb-[18px] pt-[22px] shadow-2xl shadow-black/50">
              <View className="absolute left-10 right-10 top-0 h-px bg-primary opacity-50" />
              <View className="mb-3.5 items-center">
                <Text className="mb-0.5 text-[10px] font-bold uppercase tracking-[2.5px] text-[#7a6e60] no-underline">
                  Scan to download
                </Text>
                <Text className="text-xs text-[#7a6e60] no-underline">
                  Open your camera and point it here
                </Text>
              </View>
              <View className="rounded-lg bg-[#faf9f7] p-0.5 shadow-lg shadow-black/40">
                <Image
                  accessibilityLabel="QR code to download Radious"
                  className="aspect-square w-full rounded-sm"
                  resizeMode="contain"
                  source={{ uri: QR_IMAGE_URL }}
                />
              </View>
              <View className="mt-3 flex-row items-center justify-between gap-2">
                <Text className="text-[10px] font-semibold uppercase tracking-[0.6px] text-[#7a6e60] no-underline">
                  radious.app/download
                </Text>
                <Text className="text-right text-[11px] text-[#7a6e60] no-underline">
                  Free · iPhone
                </Text>
              </View>
            </View>
          </View>
        </View>

        <View className="mt-2 gap-2 border-t border-white/10 pt-3.5 md:flex-row md:items-center md:justify-between md:gap-4">
          <Text className="text-xs text-[#7a6e60] no-underline">
            © 2026 Radious, San Antonio, TX
          </Text>
          <View className="flex-row flex-wrap gap-5">
            <FooterLink label="Privacy" onPress={() => push('/privacy')} />
            <FooterLink label="Terms" onPress={() => push('/terms-of-service')} />
            <FooterLink
              label="Status"
              onPress={() => openExternalURL('https://stats.uptimerobot.com/CFnDDHkwDJ')}
            />
            <FooterLink label="Contact" onPress={() => push('/support')} />
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

function FeatureItem({ label }: { label: string }) {
  return (
    <View className="flex-row items-start gap-2.5">
      <View className="mt-2 size-[5px] rounded-full bg-primary" />
      <Text className="flex-1 text-sm leading-[21px] text-[#c4b69e] no-underline">{label}</Text>
    </View>
  );
}

function FooterLink({ label, onPress }: { label: string; onPress: () => void }) {
  return (
    <Pressable accessibilityRole="link" onPress={onPress}>
      <Text className="text-xs text-[#7a6e60] no-underline">{label}</Text>
    </Pressable>
  );
}

function AppleIcon({ color }: { color: string }) {
  return (
    <Svg width={16} height={16} viewBox="0 0 24 24" aria-hidden>
      <Path
        fill={color}
        d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"
      />
    </Svg>
  );
}

function PlayIcon({ color }: { color: string }) {
  return (
    <Svg width={16} height={16} viewBox="0 0 24 24" aria-hidden>
      <Path
        fill={color}
        d="M3 2.5v19l10.5-9.5L3 2.5Zm12.2 10.4 2.6 2.3c1.2-1 2.5-2.1 3.7-3.2-1.2-1-2.5-2.1-3.7-3.2l-2.6 2.3Zm-1.3 1.2L4.8 22l11.2-6.2-2.1-1.7Zm2.1-5.9L4.8 2 13.9 9.8 16 8.1Z"
      />
    </Svg>
  );
}

function CopyIcon({ color }: { color: string }) {
  return (
    <Svg width={11} height={11} viewBox="0 0 16 16" aria-hidden>
      <Path
        fill={color}
        d="M10.5 1.5A1.5 1.5 0 0 1 12 3v7a1.5 1.5 0 0 1-1.5 1.5h-5A1.5 1.5 0 0 1 4 10V3A1.5 1.5 0 0 1 5.5 1.5h5Zm0 1h-5a.5.5 0 0 0-.5.5v7a.5.5 0 0 0 .5.5h5a.5.5 0 0 0 .5-.5V3a.5.5 0 0 0-.5-.5ZM2.5 4A.5.5 0 0 0 2 4.5v8A1.5 1.5 0 0 0 3.5 14h6a.5.5 0 0 0 0-1h-6a.5.5 0 0 1-.5-.5v-8a.5.5 0 0 0-.5-.5Z"
      />
    </Svg>
  );
}
